import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CalendarDays, Plus, Repeat2, Loader2, UserCheck } from "lucide-react";

const TYPE_OCC = {
  affectation: { label: "Affectation confirmée", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  pre_reservation: { label: "Pré-réservation", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  disponibilite: { label: "Disponibilité déclarée", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  indisponibilite: { label: "Indisponibilité", cls: "bg-slate-200 text-slate-700 border-slate-300" },
};
const STATUT_BADGE = {
  planifiee: "bg-blue-100 text-blue-800",
  en_cours: "bg-emerald-100 text-emerald-800",
  terminee: "bg-slate-200 text-slate-700",
  archivee: "bg-slate-100 text-slate-500",
};

function iso(d) {
  const valid = d instanceof Date && !isNaN(d.getTime());
  return (valid ? d : new Date()).toISOString().slice(0, 10);
}
function mondayOf(dateStr) {
  const d = new Date(((typeof dateStr === "string" && dateStr) || iso(new Date())) + "T00:00:00");
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d;
}

export default function Planning() {
  const { user } = useAuth();
  const isGE = ["admin", "gestionnaire_ge"].includes(user?.role);
  const [anchor, setAnchor] = useState(iso(new Date()));
  const [vue, setVue] = useState("semaine");
  const [filtreSalarie, setFiltreSalarie] = useState("");
  const [filtreEntreprise, setFiltreEntreprise] = useState("");
  const [data, setData] = useState(null);
  const [salaries, setSalaries] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ salarie_id: "", entreprise_id: "", libelle: "", date_debut: "", date_fin: "", heure_debut: "", heure_fin: "", type_occupation: "affectation", echeance: "", lieu: "" });
  const [replFor, setReplFor] = useState(null);
  const [candidats, setCandidats] = useState(null);
  const [replForm, setReplForm] = useState({ date_effet: "", motif: "", salarie_remplacant_id: "" });

  const range = useMemo(() => {
    if (vue === "jour") return { debut: anchor, fin: anchor };
    if (vue === "semaine") {
      const m = mondayOf(anchor);
      const e = new Date(m); e.setDate(e.getDate() + 6);
      return { debut: iso(m), fin: iso(e) };
    }
    const d = new Date(anchor + "T00:00:00");
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { debut: iso(first), fin: iso(last) };
  }, [anchor, vue]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_debut: range.debut, date_fin: range.fin };
      if (filtreSalarie) params.salarie_id = filtreSalarie;
      if (filtreEntreprise) params.entreprise_id = filtreEntreprise;
      const r = await api.get("/planning", { params });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [range.debut, range.fin, filtreSalarie, filtreEntreprise]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const [s, e] = await Promise.all([api.get("/salaries"), api.get("/entreprises")]);
        setSalaries(s.data); setEntreprises(e.data);
      } catch { /* rôle restreint */ }
    })();
  }, []);

  const createAffectation = async () => {
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === "") delete payload[k]; });
      const r = await api.post("/planning/affectations", payload);
      if (r.data.warnings?.length) toast.warning(r.data.warnings.join(" · "));
      toast.success(`${TYPE_OCC[payload.type_occupation]?.label || "Entrée"} créée`);
      setOpenNew(false); load();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "object" ? `${det.code || ""} — ${det.message}` : formatApiError(det));
    }
  };

  const openRemplacement = async (mission) => {
    setReplFor(mission);
    setReplForm({ date_effet: mission.date_fin, motif: "", salarie_remplacant_id: "" });
    setCandidats(null);
    try {
      const r = await api.get(`/affectations/${mission.id}/candidats-remplacement`);
      setCandidats(r.data.candidats);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitRemplacement = async () => {
    try {
      const r = await api.post(`/affectations/${replFor.id}/organiser-remplacement`, replForm);
      toast.success(`Remplacement créé — ${r.data.affectation_remplacement.id.slice(0, 8)}`);
      setReplFor(null); load();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "object" ? `${det.code || ""} — ${det.message}` : formatApiError(det));
    }
  };

  const salLabel = (id) => { const s = salaries.find(x => x.id === id); return s ? `${s.prenom} ${s.nom}` : (id ? id.slice(0, 8) : "—"); };
  const entLabel = (id) => entreprises.find(x => x.id === id)?.raison_sociale || "—";

  return (
    <div data-testid="planning-page">
      <PageHeader
        title="Planning des affectations"
        subtitle="Dossier salarié unique — interventions distinctes. Le planning ne crée jamais d'heures réalisées."
        actions={isGE && (
          <Button onClick={() => setOpenNew(true)} data-testid="planning-new-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
            <Plus className="h-4 w-4 mr-2" /> Nouvelle entrée
          </Button>
        )}
      />

      <div className="flex flex-wrap items-end gap-3 mb-4 bg-white border border-slate-200 rounded-xl p-4">
        <div>
          <Label className="text-xs">Vue</Label>
          <Select value={vue} onValueChange={setVue}>
            <SelectTrigger className="w-32" data-testid="planning-vue"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="jour">Jour</SelectItem>
              <SelectItem value="semaine">Semaine</SelectItem>
              <SelectItem value="mois">Mois</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Date de référence</Label>
          <Input type="date" value={anchor} onChange={e => setAnchor(e.target.value || iso(new Date()))} className="w-40" data-testid="planning-anchor" />
        </div>
        {salaries.length > 0 && (
          <div>
            <Label className="text-xs">Salarié</Label>
            <Select value={filtreSalarie || "__all__"} onValueChange={v => setFiltreSalarie(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="planning-filtre-salarie"><SelectValue placeholder="Tous" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tous</SelectItem>
                {salaries.map(s => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {entreprises.length > 0 && isGE && (
          <div>
            <Label className="text-xs">Entreprise</Label>
            <Select value={filtreEntreprise || "__all__"} onValueChange={v => setFiltreEntreprise(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="planning-filtre-entreprise"><SelectValue placeholder="Toutes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Toutes</SelectItem>
                {entreprises.map(e => <SelectItem key={e.id} value={e.id}>{e.raison_sociale}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="text-sm text-slate-500 ml-auto" data-testid="planning-periode">
          <CalendarDays className="h-4 w-4 inline mr-1" />{range.debut} → {range.fin}
        </div>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div> :
        !data || (data.affectations.length === 0 && data.absences.length === 0) ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center" data-testid="planning-empty">
            <CalendarDays className="h-10 w-10 mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500">Aucune entrée sur cette période.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="planning-list">
            {data.absences.map(a => (
              <div key={`abs-${a.id}`} className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center justify-between" data-testid={`planning-abs-${a.id}`}>
                <div>
                  <span className="text-xs px-2 py-0.5 rounded border bg-slate-200 text-slate-700 border-slate-300 mr-2">Absence</span>
                  <span className="font-medium text-sm">{salLabel(a.salarie_id)}</span>
                  <span className="text-xs text-slate-500 ml-2">{a.date_debut} → {a.date_fin}</span>
                </div>
                <Badge variant="outline">{a.type}</Badge>
              </div>
            ))}
            {data.affectations.map(m => {
              const occ = TYPE_OCC[m.type_occupation] || TYPE_OCC.affectation;
              return (
                <div key={m.id} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`planning-item-${m.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className={`text-xs px-2 py-0.5 rounded border ${occ.cls} mr-2`}>{occ.label}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUT_BADGE[m.statut] || ""} mr-2`}>{m.statut}</span>
                      <span className="font-medium text-sm">{m.libelle}</span>
                      <div className="text-xs text-slate-500 mt-1">
                        {salLabel(m.salarie_id)} · {entLabel(m.entreprise_id)} · {m.date_debut} → {m.date_fin}
                        {m.heure_debut && ` · ${m.heure_debut}-${m.heure_fin}`}
                        {m.type_occupation === "pre_reservation" && m.echeance && ` · échéance ${m.echeance}`}
                        {m.remplacement_de && " · remplacement"}
                        {m.remplacee_par && " · remplacée"}
                      </div>
                    </div>
                    {isGE && !["terminee", "archivee", "desactivee"].includes(m.statut) && (
                      <Button size="sm" variant="outline" onClick={() => openRemplacement(m)} data-testid={`planning-remplacer-${m.id}`}>
                        <Repeat2 className="h-3.5 w-3.5 mr-1" /> Organiser un remplacement
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="max-w-2xl" data-testid="planning-new-dialog" aria-describedby="planning-new-desc">
          <DialogHeader>
            <DialogTitle>Nouvelle entrée planning</DialogTitle>
            <DialogDescription id="planning-new-desc">Contrôles serveur : disponibilité, absences, compétences, chevauchements. Aucune heure réalisée n'est créée.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.type_occupation} onValueChange={v => setForm({ ...form, type_occupation: v })}>
                <SelectTrigger data-testid="planning-new-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="affectation">Affectation confirmée</SelectItem>
                  <SelectItem value="pre_reservation">Pré-réservation</SelectItem>
                  <SelectItem value="disponibilite">Disponibilité déclarée</SelectItem>
                  <SelectItem value="indisponibilite">Indisponibilité</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Libellé *</Label><Input required value={form.libelle} onChange={e => setForm({ ...form, libelle: e.target.value })} data-testid="planning-new-libelle" /></div>
            <div className="space-y-2">
              <Label>Salarié *</Label>
              <Select value={form.salarie_id} onValueChange={v => setForm({ ...form, salarie_id: v })}>
                <SelectTrigger data-testid="planning-new-salarie"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{salaries.map(s => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Entreprise *</Label>
              <Select value={form.entreprise_id} onValueChange={v => setForm({ ...form, entreprise_id: v })}>
                <SelectTrigger data-testid="planning-new-entreprise"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{entreprises.map(e => <SelectItem key={e.id} value={e.id}>{e.raison_sociale}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Début *</Label><Input type="date" value={form.date_debut} onChange={e => setForm({ ...form, date_debut: e.target.value })} data-testid="planning-new-debut" /></div>
            <div className="space-y-2"><Label>Fin *</Label><Input type="date" value={form.date_fin} onChange={e => setForm({ ...form, date_fin: e.target.value })} data-testid="planning-new-fin" /></div>
            <div className="space-y-2"><Label>Heure début</Label><Input type="time" value={form.heure_debut} onChange={e => setForm({ ...form, heure_debut: e.target.value })} /></div>
            <div className="space-y-2"><Label>Heure fin</Label><Input type="time" value={form.heure_fin} onChange={e => setForm({ ...form, heure_fin: e.target.value })} /></div>
            {form.type_occupation === "pre_reservation" && (
              <div className="space-y-2 col-span-2"><Label>Échéance de la pré-réservation *</Label><Input type="date" value={form.echeance} onChange={e => setForm({ ...form, echeance: e.target.value })} data-testid="planning-new-echeance" />
                <p className="text-xs text-amber-700">Une pré-réservation exige un responsable (vous) et une échéance — elle ne bloque pas indéfiniment la ressource.</p></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)}>Annuler</Button>
            <Button onClick={createAffectation} data-testid="planning-new-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!replFor} onOpenChange={v => { if (!v) setReplFor(null); }}>
        <DialogContent className="max-w-2xl" data-testid="planning-remplacement-dialog" aria-describedby="planning-repl-desc">
          <DialogHeader>
            <DialogTitle>Organiser un remplacement</DialogTitle>
            <DialogDescription id="planning-repl-desc">
              L'affectation d'origine sera terminée à la date d'effet. Les heures et signatures antérieures sont conservées et jamais transférées. Le contrat de travail n'est pas affecté.
            </DialogDescription>
          </DialogHeader>
          {replFor && (
            <div className="space-y-4">
              <div className="text-sm bg-slate-50 border rounded p-3">
                <strong>{replFor.libelle}</strong> — {replFor.date_debut} → {replFor.date_fin}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Date d'effet *</Label><Input type="date" value={replForm.date_effet} onChange={e => setReplForm({ ...replForm, date_effet: e.target.value })} data-testid="repl-date-effet" /></div>
                <div className="space-y-2"><Label>Motif opérationnel *</Label><Input value={replForm.motif} onChange={e => setReplForm({ ...replForm, motif: e.target.value })} placeholder="Ex. indisponibilité déclarée" data-testid="repl-motif" /></div>
              </div>
              <div>
                <Label>Salarié remplaçant *</Label>
                {!candidats ? <div className="py-4 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-[#C5A059]" /></div> : (
                  <div className="mt-2 max-h-56 overflow-y-auto border rounded divide-y" data-testid="repl-candidats">
                    {candidats.map(c => (
                      <label key={c.salarie_id} className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${!c.disponible ? "opacity-60" : ""}`}>
                        <input type="radio" name="repl" disabled={!c.disponible}
                          checked={replForm.salarie_remplacant_id === c.salarie_id}
                          onChange={() => setReplForm({ ...replForm, salarie_remplacant_id: c.salarie_id })}
                          data-testid={`repl-candidat-${c.salarie_id}`} />
                        <UserCheck className={`h-4 w-4 ${c.disponible ? "text-emerald-600" : "text-slate-400"}`} />
                        <span className="font-medium">{c.prenom} {c.nom}</span>
                        <span className="text-xs text-slate-500">{c.poste || "—"}</span>
                        {c.blocages.length > 0 && <span className="text-xs text-red-600 ml-auto">{c.blocages.join(" · ")}</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplFor(null)}>Annuler</Button>
            <Button onClick={submitRemplacement} disabled={!replForm.salarie_remplacant_id || !replForm.motif.trim()} data-testid="repl-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
              Valider le remplacement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
